import type { ActionContext } from 'vuex';
import {
    getAnalysisBundle,
    getAvailableAnalyses,
    getCurrentPredictPendoUser,
    getPendoFieldPrefix,
    predictPendoLogin,
    type CategoryField,
    type PredictPendoAnalysis,
} from './predict.utils';

// Module load lifecycle. Kept as plain strings so the page can compare against
// them without importing an enum.
export const PREDICT_STATUS = {
    IDLE: 'idle',
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error',
    UNAVAILABLE: 'unavailable',
} as const;

export type PredictStatus = (typeof PREDICT_STATUS)[keyof typeof PREDICT_STATUS];

export interface PredictState {
    appId: string | null;
    availableAppIds: string[];
    // The Pendo subscription id the current state was loaded for. Lets `init`
    // detect a subscription switch and re-run instead of serving stale context.
    subId: string | null;
    analysisId: string | null;
    userId: string | null;
    customization: Record<string, unknown> | null;
    pendoFieldPrefix: string | null;
    categoryFields: CategoryField[];
    status: PredictStatus;
    error: unknown;
    // Flips true once available-analyses resolves with data, so the page can drop
    // its full-page loader and let the components mount + handle their own loading
    // while the rest of the handshake (login, bundle, field prefix) finishes.
    analysesLoaded: boolean;
}

// Resolve the Pendo subscription/app context. We're running inside the Pendo app,
// so the active subscription id is the pendoSubscriptionId; a URL query param
// overrides it (handy for local testing). pendoAppId is optional — login uses the
// appId from the selected analysis pair.
function readPendoContext (activeSubId?: string | null): { subId: string | null; appId: string | null } {
    const params = new URLSearchParams(window.location.search);

    return {
        subId: params.get('pendoSubscriptionId') || activeSubId || null,
        appId: params.get('pendoAppId'),
    };
}

// Pull the useful bits out of an axios-style error for logging (status + body).
function httpErrorInfo (error: unknown): { status?: number; data?: unknown } {
    const err = error as { response?: { status?: number; data?: unknown } };

    return { status: err?.response?.status, data: err?.response?.data };
}

// Auto-select the first provisioned analysis, preferring one scoped to the
// embedding's appId when present.
function pickAnalysis (
    analyses: PredictPendoAnalysis[],
    preferredAppId: string | null,
): PredictPendoAnalysis | null {
    if (!analyses.length) {
        return null;
    }

    if (preferredAppId) {
        return analyses.find((analysis) => analysis.appId === preferredAppId) ?? analyses[0];
    }

    return analyses[0];
}

export const predict = {
    namespaced: true,
    state: (): PredictState => ({
        appId: null,
        availableAppIds: [],
        subId: null,
        analysisId: null,
        userId: null,
        customization: null,
        pendoFieldPrefix: null,
        categoryFields: [],
        status: PREDICT_STATUS.IDLE,
        error: null,
        analysesLoaded: false,
    }),
    mutations: {
        setStatus (state: PredictState, status: PredictStatus) {
            state.status = status;
        },
        setContext (
            state: PredictState,
            { appId, analysisId, subId }: { appId: string | null; analysisId: string | null; subId: string | null },
        ) {
            state.appId = appId;
            state.analysisId = analysisId;
            state.subId = subId;
        },
        setAvailableAppIds (state: PredictState, appIds: string[]) {
            state.availableAppIds = appIds;
        },
        setUserId (state: PredictState, userId: string | null) {
            state.userId = userId;
        },
        setCustomization (state: PredictState, customization: Record<string, unknown> | null) {
            state.customization = customization;
        },
        setPendoFields (state: PredictState, { pendoFieldPrefix, categoryFields }: { pendoFieldPrefix: string | null; categoryFields: CategoryField[] }) {
            state.pendoFieldPrefix = pendoFieldPrefix;
            state.categoryFields = categoryFields;
        },
        setError (state: PredictState, error: unknown) {
            state.error = error;
        },
        setAnalysesLoaded (state: PredictState, analysesLoaded: boolean) {
            state.analysesLoaded = analysesLoaded;
        },
    },
    actions: {
        // Predict Light handshake: discover -> login -> confirm session -> load
        // analysis data. Resolves once customization is in state; the page gates
        // its aggregations on `isReady`.
        async init (
            { state, commit, rootGetters }: ActionContext<PredictState, unknown>,
            { reload = false }: { reload?: boolean } = {},
        ) {
            const activeSubId = rootGetters['subscription/getActiveSub']?.id ?? null;
            const { subId, appId: contextAppId } = readPendoContext(activeSubId);

            // Skip re-running when already resolved for this same subscription,
            // whether it landed on READY or UNAVAILABLE; a subscription switch
            // changes subId, so fall through and re-handshake. Re-running while
            // UNAVAILABLE would clear analysesLoaded and flash the full-page loader
            // before resolving back to the same empty state.
            const isResolvedForSameSub =
                (state.status === PREDICT_STATUS.READY || state.status === PREDICT_STATUS.UNAVAILABLE) &&
                state.subId === subId;

            if (isResolvedForSameSub && !reload) {
                return;
            }

            commit('setStatus', PREDICT_STATUS.LOADING);
            commit('setError', null);
            commit('setAnalysesLoaded', false);

            try {
                if (!subId) {
                    throw new Error('Predict handshake is missing pendoSubscriptionId');
                }

                const analyses = await getAvailableAnalyses(subId);

                commit('setAvailableAppIds', [...new Set(analyses.map((analysis) => analysis.appId))]);
                commit('setAnalysesLoaded', true);

                // No provisioned analyses means Risk Advisor is connected to a
                // different subscription: surface the dedicated unavailable state
                // rather than the generic handshake error.
                if (!analyses.length) {
                    commit('setContext', { appId: null, analysisId: null, subId });
                    commit('setUserId', null);
                    commit('setCustomization', null);
                    commit('setPendoFields', { pendoFieldPrefix: null, categoryFields: [] });
                    commit('setError', null);
                    commit('setStatus', PREDICT_STATUS.UNAVAILABLE);

                    return;
                }

                const selected = pickAnalysis(analyses, contextAppId);

                if (!selected) {
                    throw new Error('Predict handshake found no provisioned analysis');
                }

                const { appId, analysisId } = selected;

                await predictPendoLogin(subId, appId);

                const currentUser = await getCurrentPredictPendoUser();
                commit('setUserId', currentUser?.id ?? null);

                commit('setContext', { appId, analysisId, subId });

                const bundle = await getAnalysisBundle(analysisId);
                const customization = bundle?.analysis?.customization ?? null;

                if (!customization) {
                    throw new Error('Predict analysis bundle is missing customization');
                }

                commit('setCustomization', customization);

                const fieldPrefix = await getPendoFieldPrefix(analysisId);
                commit('setPendoFields', {
                    pendoFieldPrefix: fieldPrefix?.pendoFieldPrefix ?? null,
                    categoryFields: fieldPrefix?.categoryFields ?? [],
                });

                commit('setStatus', PREDICT_STATUS.READY);
            } catch (error) {
                // Log the failure and surface an error state. The mock customization
                // keeps the score labels/colors resolvable so the error/empty states
                // render with the right copy, but the page reports ERROR so the table
                // shows its error message and the dashboard shows the empty overview.
                console.error('[predict] handshake failed', httpErrorInfo(error), error);

                commit('setContext', { appId: null, analysisId: null, subId: null });
                commit('setAvailableAppIds', []);
                commit('setUserId', null);
                commit('setCustomization', null);
                commit('setPendoFields', { pendoFieldPrefix: null, categoryFields: [] });
                commit('setError', error);
                commit('setStatus', PREDICT_STATUS.ERROR);
            }
        },
    },
    getters: {
        appId: (state: PredictState) => state.appId,
        availableAppIds: (state: PredictState) => state.availableAppIds,
        analysisId: (state: PredictState) => state.analysisId,
        userId: (state: PredictState) => state.userId,
        customization: (state: PredictState) => state.customization,
        pendoFieldPrefix: (state: PredictState) => state.pendoFieldPrefix,
        categoryFields: (state: PredictState) => state.categoryFields,
        isReady: (state: PredictState) => state.status === PREDICT_STATUS.READY,
        hasError: (state: PredictState) => state.status === PREDICT_STATUS.ERROR,
        isUnavailable: (state: PredictState) => state.status === PREDICT_STATUS.UNAVAILABLE,
        // available-analyses hasn't resolved yet, so we don't know whether to show
        // the filters/overview/table or the unavailable state. The page shows a
        // single loader until then; once analyses come back with data the
        // components mount and handle their own loading while the rest of the
        // handshake (login, bundle, field prefix) finishes.
        isInitializing: (state: PredictState) =>
            !state.analysesLoaded &&
            (state.status === PREDICT_STATUS.IDLE || state.status === PREDICT_STATUS.LOADING),
    },
};

export default predict;
